import { getPlayerImageUrl, formatPlayerRoleLabel } from "../../../helper/helper";
import type { IPlayersTeamSquad, InningScorecard } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { fullName, isDismissed, norm, roleTags, teamRefs } from "./helpers";
import type { MatchInfo } from "./helpers";
import { Avatar, Crest, Empty, PlayerLink } from "./parts";

type Props = { match?: MatchInfo; innings: InningScorecard[]; squads: (IPlayersTeamSquad | undefined)[]; loading: boolean; matchLive: boolean };

const SquadsTab = ({ match, innings, squads, loading, matchLive }: Props) => {
  const teams = teamRefs(match);
  const any = squads.some((s) => s?.playingSquad?.length || s?.bench?.length);
  if (loading && !any) return <Empty>Loading squads…</Empty>;
  if (!any) return <Empty>Squads have not been announced for this match yet.</Empty>;

  return (
    <div className="pteams">
      {teams.map((team, ti) => {
        const squad = squads[ti];
        const xi = squad?.playingSquad?.length ? squad.playingSquad : [];
        const bench = squad?.bench ?? [];
        const batInns = innings.filter((i) => norm(i.battingTeamName) === norm(team.name));
        const bowlInns = innings.filter((i) => norm(i.bowlingTeamName) === norm(team.name));
        const perf = (id: string) => {
          const bat = batInns.flatMap((i) => i.battingScoreCardResponseList ?? []).find((b) => b.playerId === id);
          const bowl = bowlInns.flatMap((i) => i.bowlingScoreCardResponses ?? []).find((b) => b.playerId === id);
          return { bat, bowl };
        };
        const noBatYet = matchLive ? "YET TO BAT" : "DID NOT BAT";
        return (
          <div className="box" key={team.name || ti}>
            <div className="box-h">
              <Crest team={team} size={44} />
              <h3>{team.name || `Team ${ti + 1}`}</h3>
              <span className="sub">{xi.length ? "Playing XI" : "Squad"}</span>
            </div>
            {xi.length ? (
              <ul className="xi">
                {xi.map((p, i) => {
                  const name = fullName(p.playerFirstName, p.playerLastName) || "Player";
                  const tags = roleTags(p.playerRole);
                  const { bat, bowl } = perf(p.playerId);
                  const role = formatPlayerRoleLabel(p.playerRole);
                  return (
                    <li key={p.playerId || i}>
                      <span className="no">{i + 1}</span>
                      <Avatar name={name} team={team.name} src={getPlayerImageUrl(p.imageUri)} />
                      <span>
                        <b>
                          <PlayerLink id={p.playerId} name={name} />
                          {tags.c ? <span className="tag">C</span> : null}
                          {tags.vc ? <span className="tag">VC</span> : null}
                          {tags.wk ? <span className="tag">WK</span> : null}
                        </b>
                        {role && role !== "-" && !tags.c && !tags.wk && !tags.vc ? <span className="ro">{role}</span> : null}
                      </span>
                      <span className="now">
                        {bat ? (
                          <>
                            <em>BAT</em>
                            {bat.run ?? 0}
                            {isDismissed(bat) ? "" : "*"} ({bat.balls ?? 0})
                          </>
                        ) : null}
                        {bat && bowl ? <>&nbsp;&nbsp;</> : null}
                        {bowl ? (
                          <>
                            <em>BOWL</em>
                            {bowl.wickets ?? 0}/{bowl.runs ?? 0}
                          </>
                        ) : null}
                        {!bat && !bowl ? <em>{innings.length ? noBatYet : ""}</em> : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Empty>Playing XI not announced yet.</Empty>
            )}
            {bench.length ? (
              <div className="dnb">
                <b>Bench</b>
                <span className="bench">
                  {bench.map((p) => (
                    <span key={p.playerId}>
                      <PlayerLink id={p.playerId} name={fullName(p.playerFirstName, p.playerLastName) || "Player"} />
                    </span>
                  ))}
                </span>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default SquadsTab;
